import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import PageAdvice from "../components/PageAdvice";
import {
  MagnifyingGlass, CircleNotch, CaretLeft, CaretRight, ArrowRight,
  User, Buildings, AddressBook, ArrowsClockwise, CheckCircle, ProhibitInset,
} from "@phosphor-icons/react";

const fmtSync = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "never");

export default function ClientsList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [syncing, setSyncing] = useState(false);
  const debounce = useRef(null);
  const limit = 25;

  const load = useCallback(async (p, q, st) => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients", { params: { page: p, limit, search: q, status: st } });
      setData(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(1, "", "all"); }, [load]);

  const onSearch = (v) => {
    setSearch(v);
    setPage(1);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(1, v, statusFilter), 350);
  };

  const onFilter = (st) => { setStatusFilter(st); setPage(1); load(1, search, st); };

  const goPage = (p) => { setPage(p); load(p, search, statusFilter); };

  const refresh = async () => {
    setSyncing(true);
    try {
      const { data: s } = await api.post("/clients/sync");
      const parts = [];
      if (s.added) parts.push(`${s.added} added`);
      if (s.reactivated) parts.push(`${s.reactivated} restored`);
      if (s.removed) parts.push(`${s.removed} flagged removed`);
      toast.success(parts.length ? `Synced — ${parts.join(", ")}.` : `Synced — no changes (${s.total_active} active).`);
      load(page, search, statusFilter);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Layout>
      <div data-testid="clients-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-sm uppercase tracking-widest font-semibold text-neutral-500">Aroflo · Clients Directory</p>
            <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-black tracking-tighter text-[#0A0A0A]">Clients</h1>
            {data && (
              <p className="mt-1 text-sm text-neutral-500" data-testid="clients-last-synced">Last synced: {fmtSync(data.last_synced)}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {data && (
              <span className="rounded-sm bg-[#0A0A0A] px-3 py-1.5 text-sm font-semibold text-white" data-testid="clients-count">
                {(data.active_count ?? data.count).toLocaleString()} active
              </span>
            )}
            {isAdmin && (
              <button
                data-testid="clients-refresh-btn"
                onClick={refresh}
                disabled={syncing}
                className="inline-flex items-center gap-2 rounded-sm bg-[#0055FF] px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:bg-[#0044CC] disabled:opacity-60"
              >
                <ArrowsClockwise size={16} weight="bold" className={syncing ? "animate-spin" : ""} />
                {syncing ? "Refreshing…" : "Refresh from Aroflo"}
              </button>
            )}
          </div>
        </div>

        {/* Status filter */}
        <div className="mt-5 flex flex-wrap gap-2" data-testid="clients-status-filter">
          {[
            { v: "all", label: "All", count: data ? (data.active_count + data.removed_count) : null },
            { v: "active", label: "Active", count: data?.active_count },
            { v: "removed", label: "Removed", count: data?.removed_count },
          ].map((f) => (
            <button
              key={f.v}
              data-testid={`clients-filter-${f.v}`}
              onClick={() => onFilter(f.v)}
              className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-all duration-200 ${
                statusFilter === f.v ? "bg-[#0A0A0A] text-white" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"
              }`}
            >
              {f.label}{f.count != null ? ` · ${f.count}` : ""}
            </button>
          ))}
        </div>

        <div className="relative mt-4 max-w-xl">
          <MagnifyingGlass size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            data-testid="client-search-input"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search clients by name…"
            className="w-full rounded-sm border border-neutral-300 bg-white py-2.5 pl-11 pr-4 text-base outline-none transition-all duration-200 focus:border-[#0055FF] focus:ring-2 focus:ring-[#0055FF]/20"
          />
        </div>

        <div className="mt-5 border border-neutral-200 bg-white shadow-sm rounded-sm">
          {loading ? (
            <div className="flex h-64 items-center justify-center" data-testid="clients-loading"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div>
          ) : !data?.items.length ? (
            <div className="flex h-64 flex-col items-center justify-center gap-2 text-neutral-500" data-testid="clients-empty">
              <AddressBook size={28} className="text-neutral-300" /> No clients match “{search}”.
            </div>
          ) : (
            <div className="overflow-x-auto" data-testid="clients-table">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
                    <th className="px-5 py-3 text-left">Client Name</th>
                    <th className="px-5 py-3 text-left">Type</th>
                    <th className="px-5 py-3 text-left">Status</th>
                    <th className="px-5 py-3 text-left">Aroflo ID</th>
                    <th className="px-5 py-3 text-right">Report</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((c) => (
                    <tr
                      key={c.id}
                      data-testid={`client-row-${c.id}`}
                      onClick={() => navigate(`/clients/${c.id}`)}
                      className={`group cursor-pointer border-b border-neutral-100 even:bg-neutral-50/60 hover:bg-[#0055FF]/[0.04] ${c.status === "removed" ? "opacity-60" : ""}`}
                    >
                      <td className="px-5 py-3">
                        <button
                          data-testid={`client-link-${c.id}`}
                          onClick={(e) => { e.stopPropagation(); navigate(`/clients/${c.id}`); }}
                          className="font-semibold text-[#0A0A0A] group-hover:text-[#0055FF] hover:underline"
                        >
                          {c.name || "—"}
                        </button>
                      </td>
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center gap-1.5 text-neutral-600">
                          {c.individual ? <User size={15} /> : <Buildings size={15} />}
                          {c.individual ? "Individual" : "Company"}
                        </span>
                      </td>
                      <td className="px-5 py-3" data-testid={`client-status-${c.id}`}>
                        {c.status === "removed" ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#DC2626]/10 px-2.5 py-1 text-xs font-semibold text-[#DC2626]" title={c.removed_at ? `Removed ${fmtSync(c.removed_at)}` : "Removed"}>
                            <ProhibitInset size={13} weight="bold" /> Removed
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#059669]/10 px-2.5 py-1 text-xs font-semibold text-[#059669]">
                            <CheckCircle size={13} weight="fill" /> Active
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 font-mono text-neutral-500">{c.id}</td>
                      <td className="px-5 py-3 text-right">
                        <ArrowRight size={18} className="ml-auto text-neutral-300 transition-all duration-200 group-hover:translate-x-1 group-hover:text-[#0055FF]" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data && data.items.length > 0 && (
            <div className="flex items-center justify-between border-t border-neutral-200 px-5 py-3">
              <span className="text-sm text-neutral-500" data-testid="page-info">Page {data.page} of {data.pages}</span>
              <div className="flex gap-2">
                <button data-testid="prev-page" disabled={page <= 1} onClick={() => goPage(page - 1)} className="flex items-center gap-1 rounded-sm border border-neutral-300 bg-white px-3 py-1.5 text-sm font-semibold disabled:opacity-40 hover:bg-neutral-50">
                  <CaretLeft size={15} /> Prev
                </button>
                <button data-testid="next-page" disabled={page >= data.pages} onClick={() => goPage(page + 1)} className="flex items-center gap-1 rounded-sm border border-neutral-300 bg-white px-3 py-1.5 text-sm font-semibold disabled:opacity-40 hover:bg-neutral-50">
                  Next <CaretRight size={15} />
                </button>
              </div>
            </div>
          )}
        </div>
        <PageAdvice items={[
          "This list is served from the Hub database, not live Aroflo. Click \u201cRefresh from Aroflo\u201d to pull new clients and update the table.",
          "Clients that no longer exist in Aroflo are flagged \u201cRemoved\u201d (with a date) and kept for audit \u2014 they are never deleted. Use the Active/Removed filter to review them.",
          "Open any client to load their full, live record (cached afterwards for fast reloads).",
        ]} />
      </div>
    </Layout>
  );
}
